import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../../buttons/Button'
import { Card } from '../../../card/Card'
import Icon from '../../../ui/Icon'
import { LockerSvg } from '../../../ui/lockerSvg/LockerSvg'

import { useLockerWidget } from '../../../../hooks/widget/dashboard/useLockerWidget'
import { useWidgetErrorBanner } from '../../../../hooks/widget/useWidgetErrorBanner'
import { useApp } from '../../../../contexts/AppContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { LockerStatus } from '../../../../types/widget/widget'

type LockerAccessWidgetProps = {
  bookingStartTime?: string
}

const ACCESS_AVAILABLE_BEFORE_MS = 15 * 60 * 1000

const formatCode = (code: string) => {
  const digits = code.replace(/\D/g, '')

  return digits.replace(/(\d{3})(?=\d)/g, '$1-')
}

export const LockerAccessWidget = ({
  bookingStartTime,
}: LockerAccessWidgetProps) => {
  const { t } = useTranslation('dashboard')
  const { showBanner } = useApp()
  const lockerResult = useLockerWidget()
  const handleError = useWidgetErrorBanner()
  const { data, loading, error } = lockerResult

  const bookingStart = bookingStartTime
    ? new Date(bookingStartTime).getTime()
    : Number.NaN

  const availableFrom = bookingStart - ACCESS_AVAILABLE_BEFORE_MS
  const available =
    Number.isFinite(availableFrom) && Date.now() >= availableFrom
  const active = available && data != null
  const open = active && data.status === LockerStatus.OPEN

  useEffect(() => {
    if (error) {
      handleError(error)
    }
  }, [error, handleError])

  if (!bookingStartTime) {
    return null
  }

  const showAccessInstructions = (lockerNumber: number, lockerCode: string) => {
    showBanner({
      kind: BannerKind.INFO,
      msg: t('dashboard.locker-access.instructions', {
        number: lockerNumber,
        code: formatCode(lockerCode),
      }),
    })
  }

  return (
    <Card className="col-12 col-xl-6">
      <div className="locker-access">
        <div className="locker-access-header">
          <div className="eyebrow">{t('dashboard.locker-access.eyebrow')}</div>
        </div>

        <div className="locker-access-illustration">
          <span
            className={`badge locker-access-availability ${
              !active ? 'badge-warning' : open ? 'badge-success' : ''
            }`}
          >
            {active && <span className="locker-access-status-dot" />}
            {t(
              !available
                ? 'dashboard.locker-access.available-before'
                : !data
                  ? 'dashboard.locker-access.unavailable'
                  : open
                    ? 'dashboard.locker-access.status.open'
                    : 'dashboard.locker-access.status.closed'
            )}
          </span>

          <LockerSvg open={open} />
        </div>

        <div className="locker-access-content">
          <h3>
            {data
              ? t('dashboard.locker-access.number', {
                  number: data.number,
                })
              : t('dashboard.locker-access.unassigned')}
          </h3>

          <div className="locker-access-code-panel">
            <span className="locker-access-code-label">
              {t('dashboard.locker-access.code')}
            </span>

            <strong
              className={`locker-access-code${active ? '' : ' is-disabled'}`}
              aria-disabled={!active}
            >
              {data ? formatCode(data.code) : '•••-•••'}
            </strong>
          </div>
        </div>

        <Button
          block
          variant="primary"
          icon={<Icon name="info" size={15} />}
          label={t('dashboard.locker-access.access-instructions')}
          loading={loading}
          disabled={!active || loading}
          onClick={() => data && showAccessInstructions(data.number, data.code)}
        />
      </div>
    </Card>
  )
}
